import { authFetch } from '../utils/auth';

/**
 * Fetch the list of memories for the current user.
 * @param {Object} params - Query parameters
 * @param {string} [params.category] - Filter by category
 * @param {number} [params.limit=100] - Max results (1-500)
 * @param {number} [params.offset=0] - Pagination offset
 * @returns {Promise<Object>} { memories, total, limit, offset }
 */
export const fetchMemories = async ({ category, limit = 100, offset = 0 } = {}) => {
  const queryParams = new URLSearchParams();
  if (category) queryParams.append('category', category);
  if (limit) queryParams.append('limit', limit);
  if (offset) queryParams.append('offset', offset);

  const response = await authFetch(`/api/memories?${queryParams.toString()}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch memories: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Get a single memory by ID.
 * @param {string} memoryId
 * @returns {Promise<Object>} memory object
 */
export const fetchMemory = async (memoryId) => {
  const response = await authFetch(`/api/memories/${memoryId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch memory: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Create a new memory.
 * @param {Object} data
 * @param {string} data.content - Memory text (required, min 1 char)
 * @param {string} [data.category='other'] - Memory category
 * @param {string} [data.importance='medium'] - "high", "medium", or "low"
 * @returns {Promise<Object>} { message, memory }
 */
export const createMemory = async ({ content, category = 'other', importance = 'medium' }) => {
  const response = await authFetch('/api/memories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, category, importance }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create memory: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Update an existing memory.
 * @param {string} memoryId
 * @param {Object} updates - Fields to update (content, category, importance)
 * @returns {Promise<Object>} { message, memory }
 */
export const updateMemory = async (memoryId, updates) => {
  const response = await authFetch(`/api/memories/${memoryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to update memory: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Delete a single memory by ID.
 * @param {string} memoryId
 * @returns {Promise<Object>} { message }
 */
export const deleteMemory = async (memoryId) => {
  const response = await authFetch(`/api/memories/${memoryId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to delete memory: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Delete all memories for the current user.
 * @returns {Promise<Object>} { message, count }
 */
export const deleteAllMemories = async () => {
  const response = await authFetch('/api/memories', {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to delete all memories: ${response.statusText}`);
  }
  return response.json();
};
